import React, { useEffect, useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, ChevronDown } from 'lucide-react';

const Filters = ({ filters, setFilters, regions, categories = [] }) => {
  const [cities, setCities] = useState([]);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const datePickerRef = useRef(null);
  
  // Get current date
  const today = new Date();
  
  // Common date presets
  const datePresets = [
    { label: 'Last 7 days', startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), endDate: today },
    { label: 'Last 30 days', startDate: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), endDate: today },
    { label: 'Last 90 days', startDate: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), endDate: today },
    { label: 'This year', startDate: new Date(today.getFullYear(), 0, 1), endDate: today },
    { label: 'Last year', startDate: new Date(today.getFullYear() - 1, 0, 1), endDate: new Date(today.getFullYear() - 1, 11, 31) }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDateRangePicker(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [datePickerRef]);

  // Determine date range display text
  const getDateRangeText = () => {
    if (!filters.startDate && !filters.endDate) return 'Select date range';
    
    // Check if current selection matches a preset
    for (const preset of datePresets) {
      if (
        filters.startDate?.toDateString() === preset.startDate.toDateString() && 
        filters.endDate?.toDateString() === preset.endDate.toDateString()
      ) {
        return preset.label;
      }
    }
    
    // Custom date range
    if (filters.startDate && filters.endDate) {
      return `${filters.startDate.toLocaleDateString()} - ${filters.endDate.toLocaleDateString()}`;
    } else if (filters.startDate) {
      return `From ${filters.startDate.toLocaleDateString()}`;
    } else if (filters.endDate) {
      return `Until ${filters.endDate.toLocaleDateString()}`;
    }
    
    return 'Select date range';
  };

  // Update cities when region changes
  useEffect(() => {
    if (filters.region && regions[filters.region]) {
      setCities(regions[filters.region]);
    } else {
      setCities([]);
    }
  }, [filters.region, regions]);

  const handleSourceChange = (e) => {
    setFilters({ ...filters, source: e.target.value });
  };

  const handleRegionChange = (e) => {
    setFilters({ ...filters, region: e.target.value, city: '' });
  };

  const handleCityChange = (e) => {
    setFilters({ ...filters, city: e.target.value });
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFilters({
      ...filters,
      startDate: start,
      endDate: end
    });
  };

  const handleReplyStatusChange = (e) => {
    setFilters({ ...filters, replyStatus: e.target.value });
  };

  const handleSentimentChange = (e) => {
    setFilters({ ...filters, sentiment: e.target.value });
  };

  const handleCategoryChange = (e) => {
    setFilters({ ...filters, category: e.target.value });
  };

  const applyDatePreset = (preset) => {
    setFilters({
      ...filters,
      startDate: new Date(preset.startDate),
      endDate: new Date(preset.endDate)
    });
    setShowDateRangePicker(false);
  };

  const handleClearDates = () => {
    setFilters({
      ...filters,
      startDate: null,
      endDate: null
    });
  };

  const handleClearFilters = () => {
    setFilters({
      source: '',
      region: '',
      city: '',
      startDate: null,
      endDate: null,
      replyStatus: '',
      sentiment: '',
      category: '',
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium">Filters</h2>
        <button
          onClick={handleClearFilters}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Clear All Filters
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Improved Date Range Selector */}
        <div className="relative" ref={datePickerRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Range
          </label>
          <div 
            className="w-full border border-gray-300 rounded-md px-3 py-2 flex items-center justify-between cursor-pointer hover:border-blue-500"
            onClick={() => setShowDateRangePicker(!showDateRangePicker)}
          >
            <div className="flex items-center">
              <Calendar className="mr-2 h-4 w-4 text-gray-500" />
              <span className="text-sm">{getDateRangeText()}</span>
            </div>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </div>
          
          {/* Date Range Dropdown Panel */}
          {showDateRangePicker && (
            <div className="absolute z-10 mt-1 w-auto min-w-max bg-white rounded-md shadow-lg border border-gray-200">
              {/* Quick Select Presets (Horizontal) */}
              <div className="p-3 border-b border-gray-200">
                <div className="text-sm font-medium mb-2">Quick Select</div>
                <div className="flex flex-wrap gap-2">
                  {datePresets.map((preset, index) => (
                    <button
                      key={index}
                      className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-blue-50 hover:text-blue-600"
                      onClick={() => applyDatePreset(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Calendar Range Picker */}
              <div className="p-3">
                <DatePicker
                  selected={filters.startDate}
                  onChange={handleDateRangeChange}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  selectsRange
                  inline
                  monthsShown={2}
                  showPreviousMonths={false}
                  calendarClassName="flex"
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border-t border-gray-200 bg-gray-50">
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={handleClearDates}
                >
                  Clear
                </button>
                <button
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  onClick={() => setShowDateRangePicker(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Source/Channel Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Source
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={filters.source}
            onChange={handleSourceChange}
            disabled
          >
            <option value="NPS Response">NPS Response</option>
          </select>
        </div>
        
        {/* Region Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Region
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={filters.region}
            onChange={handleRegionChange}
          >
            <option value="">All Regions</option>
            {Object.keys(regions).map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </div>
        
        {/* City Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={filters.city}
            onChange={handleCityChange}
            disabled={!filters.region}
          >
            <option value="">All Cities</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
        
        
        
        {/* Reply Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reply Status
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={filters.replyStatus}
            onChange={handleReplyStatusChange}
          >
            <option value="">All Statuses</option>
            <option value="Replied">Replied</option>
            <option value="Not Replied">Not Replied</option>
          </select>
        </div>
        
        {/* Sentiment Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sentiment
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={filters.sentiment}
            onChange={handleSentimentChange}
          >
            <option value="">All Sentiments</option>
            <option value="Positive">Promoters (Positive)</option>
            <option value="Neutral">Passives (Neutral)</option>
            <option value="Negative">Detractors (Negative)</option>
          </select>
        </div>
        
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            value={filters.category}
            onChange={handleCategoryChange}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default Filters;